import React from "react";
import { useContext } from "react";
import {
  deleteScenarioApi,
  saveScenarioApi,
  updateData,
  updateScenarioApi,
} from "../services/client";
import { debounce, updateAtIndex } from "../utils";
import {
  Client,
  Person,
  RetirementSpendingSettings,
  SavedScenario,
  ScenarioSettings,
} from "../types";

const IncomeContext = React.createContext({
  data: {} as Client,
  storeScenarios: (_: ScenarioSettings[]) => { },
  saveScenario: (_: any) => { },
  deleteScenario: (_: number) => { },
  updateScenario: (_: number, __: Partial<ScenarioSettings>) => { },
  setPerson: (_: Person) => { },
  setField: (_: keyof Client) => (_: any) => { },
  setSpending: (_: RetirementSpendingSettings) => { },
  setLocal: (() => { }) as any,
});

const updateRemote = debounce(updateData, 500);

interface IncomeProviderProps {
  data: Client;
  setLocal: React.Dispatch<React.SetStateAction<Client>>;
  children: React.ReactNode;
}

export const IncomeProvider = ({
  data,
  setLocal,
  children,
}: IncomeProviderProps) => {
  const setData = (fn: (data: Client) => Client) => {
    console.log("setting data", data);
    setLocal((data) => {
      const result = fn(data);
      console.log("result", result);
      updateRemote(result.id, result);
      return result;
    });
  };

  const setField = (key: string) => (val: any) => {
    setData((data) => ({
      ...data,
      [key]: val,
    }));
  };

  const storeScenarios = (scenarios: ScenarioSettings[]) => {
    setData((data) => {
      const client = {
        ...data,
        scenarios,
      };
      return client;
    });
  };

  const deleteScenario = (id: number) => {
    const oldScenarios = [...data.scenarios];
    setLocal((data) => {
      const scenarios = data.scenarios.filter((s) => s.id !== id);
      return {
        ...data,
        scenarios,
      };
    });

    setField("scenarioOrder")(data.scenarioOrder.filter((i) => i !== id));

    deleteScenarioApi(id).then((resp) => {
      if (!resp.ok) {
        setLocal((data) => {
          return {
            ...data,
            scenarios: oldScenarios,
          };
        });
      }
    });
  };

  const updateScenario = (id: number, scenario: Partial<ScenarioSettings>) => {
    setLocal((data) => {
      const scenarios = data.scenarios.map((s) =>
        s.id === id ? { ...s, ...scenario } : s,
      );
      return {
        ...data,
        scenarios,
      };
    });
    updateScenarioApi(id, scenario);
  };

  const setPerson = (person: Person) => {
    setData((data) => ({
      ...data,
      people: updateAtIndex(data.people, person.id, person),
    }));
  };

  const setSpending = (spending: RetirementSpendingSettings) => {
    setData((data) => ({
      ...data,
      spending,
    }));
  };

  const saveScenario = (scenario: SavedScenario) => {
    setLocal((data) => {
      return {
        ...data,
        scenarios: [...data.scenarios, scenario],
      };
    });
    setField("scenarioOrder")([...data.scenarioOrder, scenario.id]);
    saveScenarioApi({ ...scenario, clientId: data.id });
  };

  const value = {
    data,
    storeScenarios,
    setPerson,
    setSpending,
    setField,
    setLocal,
    updateScenario,
    deleteScenario,
    saveScenario,
  };

  return (
    <IncomeContext.Provider value={value}>{children}</IncomeContext.Provider>
  );
};

export const useInfo = () => useContext(IncomeContext);
